import { throttle } from "es-toolkit";
import type { TouchEventHandler } from "react";
import { useCallback, useEffect, useRef } from "react";

const HOLD_INTERVAL = 750;
const DOUBLE_TAP_INTERVAL = 200;

type Options = {
  stopPropagation?: boolean;
  preventDefault?: boolean;
};

export type TouchProps = {
  onFirstTouch?: TouchEventHandler;
  onSecondTouch?: TouchEventHandler;
  onTap?: TouchEventHandler;
  onDoubleTap?: TouchEventHandler;
  onHold?: TouchEventHandler;
  options?: Options;
};

export const useTouch = ({ onFirstTouch, onSecondTouch, onTap, onDoubleTap, onHold, options = {} }: TouchProps) => {
  const timestampA = useRef<number>(undefined);
  const timestampB = useRef<number>(undefined);
  const throttled = useRef<ReturnType<typeof throttle>>(undefined);

  const reset = () => {
    timestampA.current = undefined;
    timestampB.current = undefined;
  };

  useEffect(() => {
    throttled.current?.cancel();
    reset();

    throttled.current = throttle(
      (event) => {
        if (
          timestampA.current &&
          timestampB.current &&
          Math.abs(timestampA.current - timestampB.current) < DOUBLE_TAP_INTERVAL
        ) {
          onDoubleTap?.(event);
        } else {
          const handler =
            timestampA?.current && event.timeStamp - timestampA.current > HOLD_INTERVAL ? (onHold ?? onTap) : onTap;
          handler?.(event);
        }

        reset();
      },
      DOUBLE_TAP_INTERVAL,
      { edges: ["trailing"] },
    );

    return () => {
      throttled.current?.cancel();
      reset();
    };
  }, [onDoubleTap, onHold, onTap]);

  const handleTouchStart: TouchEventHandler = useCallback(
    (event) => {
      if (timestampA.current) {
        timestampB.current = event.timeStamp;
        onSecondTouch?.(event);
      } else {
        timestampA.current = event.timeStamp;
        onFirstTouch?.(event);
      }

      if (options.stopPropagation) event.stopPropagation();
    },
    [onFirstTouch, onSecondTouch, options.stopPropagation],
  );

  const handleTouchEnd: TouchEventHandler = useCallback(
    (event) => {
      if (options.stopPropagation) event.stopPropagation();
      if (options.preventDefault) event.preventDefault();

      throttled.current?.(event);
    },
    [options.preventDefault, options.stopPropagation],
  );

  return {
    onTouchStart: handleTouchStart,
    onTouchEnd: handleTouchEnd,
  };
};
